import { useSceneStore } from '@/features/shared/stores/scene.store'
import { useShotListStore } from '@/features/shotlist/store/useShotListStore'
import { useWorkspace } from '@/features/workspace/stores/workspace.store'
import { ScrollArea } from '@/shared/components/ui/scroll-area'
import { cn } from '@/shared/lib/utils'
import { getSceneHeader } from '@/shared/utils/editor-utils'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useGenerateFrameImageMutation, useRegenerateFrameMutation } from '../queries/storyboard.ai.queries'
import { useSaveStoryboardMutation, useStoryboardQuery } from '../queries/storyboard.queries'
import { useStoryboardStore } from '../store/useStoryboardStore'
import { ShotListPreviewPanel } from './ShotListPreviewPanel'
import StoryboardEmptySceneState from './StoryboardEmptySceneState'
import StoryboardFrame from './StoryboardFrame'
import StoryboardGenerationDialog from './StoryboardGenerationDialog'
import StoryboardHeader from './StoryboardHeader'
import StoryboardImageLightbox from './StoryboardImageLightbox'
import StoryboardNoFramesState from './StoryboardNoFramesState'





const StoryboardMainContent = () => {
    const { project } = useWorkspace()
    const { scenes, editor } = useSceneStore()
    const {
        selectedSceneNode,
        selectedShot,
        selectShot
    } = useStoryboardStore()

    // Queries & Mutations
    const { data: frames = [], isLoading: isLoadingFrames } = useStoryboardQuery(project?.id || '', selectedSceneNode?.id ?? null)
    const { mutateAsync: generateFrameImage } = useGenerateFrameImageMutation()
    const { mutateAsync: regenerateFrame } = useRegenerateFrameMutation()
    const { mutateAsync: saveStoryboard } = useSaveStoryboardMutation(project?.id || '', selectedSceneNode?.id ?? '')

    // Local state for frame regeneration tracking
    const [regeneratingFrameIds, setRegeneratingFrameIds] = useState<Set<string>>(new Set())
    const [regeneratingFrameAIIds, setRegeneratingFrameAIIds] = useState<Set<string>>(new Set())
    // Cache-busted URLs: after regeneration, store url with t=<timestamp> to force browser re-fetch
    const [frameCacheBusters, setFrameCacheBusters] = useState<Map<string, string>>(new Map())
    const bustUrl = (url: string) => `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`

    // Shot List Store integration
    const { loadShotList, shotLists } = useShotListStore()
    const currentSceneShots = selectedSceneNode ? (shotLists[selectedSceneNode.id] || []) : []

    const [showShotList, setShowShotList] = useState(false)
    const [isGenerationDialogOpen, setIsGenerationDialogOpen] = useState(false)
    const [generatingShot, setGeneratingShot] = useState<any | null>(null)

    // Load shot list and storyboard when scene selection changes
    useEffect(() => {
        if (selectedSceneNode && project) {
            loadShotList(selectedSceneNode.id)
            selectShot(null) // Reset selected shot when scene changes
        }
    }, [selectedSceneNode, project, loadShotList, selectShot])


    const sceneTitle = editor?.read(() => {
        return selectedSceneNode ? getSceneHeader(selectedSceneNode) : "Select a Scene"
    })

    // Filter frames if a specific shot is selected
    const selectedShotFrame = selectedShot ? frames.filter(f => f.shotId === selectedShot.id) : null

    // Determine what to display
    const displayedFrames = selectedShotFrame
        ? (selectedShotFrame)
        : frames

    const [viewingImage, setViewingImage] = useState<string | null>(null)

    const handleRegenerateFrame = async (frame: any) => {
        if (!selectedSceneNode || !frame.id) return

        setRegeneratingFrameIds(prev => new Set(prev).add(frame.id))

        try {
            const prompt = frame.visualDescription
            console.log(`Regenerating frame ${frame.frameNumber} image with prompt:`, prompt)

            const { imageUrl } = await generateFrameImage({ frameId: frame.id, prompt, referenceImageUrl: frame.referenceImageUrl })

            if (imageUrl) {
                const updatedFrames = frames.map(f => f.id === frame.id ? { ...f, imageUrl } : f)
                await saveStoryboard(updatedFrames)
                // Force browser to bypass its cache for this specific frame
                setFrameCacheBusters(prev => new Map(prev).set(frame.id, bustUrl(imageUrl)))
            }
        } catch (error) {
            console.error(`Error regenerating frame ${frame.id}:`, error)
        } finally {
            setRegeneratingFrameIds(prev => {
                const next = new Set(prev)
                next.delete(frame.id)
                return next
            })
        }
    }

    const handleRegenerateFrameAI = async (frame: any) => {
        if (!selectedSceneNode) return

        setRegeneratingFrameAIIds(prev => new Set(prev).add(frame.id))

        try {
            const sceneText = editor?.read(() => selectedSceneNode.getCurrentSceneContent())?.toString() || ''
            const updatedFrame = await regenerateFrame({ frame, sceneText })

            const updatedFrames = frames.map(f => f.id === frame.id ? { ...f, ...updatedFrame } : f)
            await saveStoryboard(updatedFrames)
            // Force browser to bypass its cache for this specific frame
            if (updatedFrame.imageUrl) {
                setFrameCacheBusters(prev => new Map(prev).set(frame.id, bustUrl(updatedFrame.imageUrl!)))
            }
        } catch (error) {
            console.error(`Error regenerating frame ${frame.id}:`, error)
        } finally {
            setRegeneratingFrameAIIds(prev => {
                const next = new Set(prev)
                next.delete(frame.id)
                return next
            })
        }
    }


    const handleUpdateFrame = async (frameId: string, updates: any) => {
        if (!selectedSceneNode) return
        const updatedFrames = frames.map(f => f.id === frameId ? { ...f, ...updates } : f)
        await saveStoryboard(updatedFrames)
    }

    const handleRemoveFrame = async (frameId: string) => {
        if (!selectedSceneNode) return
        const updatedFrames = frames.filter(f => f.id !== frameId)
        await saveStoryboard(updatedFrames)
    }

    const initiateFrameGeneration = (shot: any) => {
        setGeneratingShot(shot)
        setIsGenerationDialogOpen(true)
    }

    const [recentlyCreatedFrameId, setRecentlyCreatedFrameId] = useState<string | null>(null)

    const handleAddFrame = async () => {
        if (!selectedSceneNode || !project) return

        const newFrameId = crypto.randomUUID()
        const newFrameNumber = frames.length > 0 ? Math.max(...frames.map(f => f.frameNumber)) + 1 : 1

        const newFrame = {
            id: newFrameId,
            projectId: project.id,
            sceneId: selectedSceneNode.id,
            shotId: selectedShot?.id ?? '',
            frameNumber: newFrameNumber,
            visualDescription: '',
            shotDescription: '',
            imagePrompt: '',
            dialogueNotes: '',
            imageUrl: '',
            createdAt: new Date(),
            updatedAt: new Date()
        }

        const updatedFrames = [...frames, newFrame]

        // Optimistically set it to editing state
        setRecentlyCreatedFrameId(newFrameId)

        await saveStoryboard(updatedFrames)
    }

    if (!selectedSceneNode) {
        return <StoryboardEmptySceneState />
    }

    return (
        <div className="flex flex-1 w-full h-full overflow-hidden bg-background min-w-0">
            <div className="flex w-full h-full overflow-hidden min-w-0">
                <div className={cn("flex flex-col h-full overflow-hidden transition-all duration-300 min-w-0", showShotList ? "w-2/3 lg:w-3/4" : "w-full")}>
                    <StoryboardHeader
                        sceneTitle={sceneTitle || "Select a Scene"}
                        displayedFramesCount={displayedFrames.length}
                        isFiltered={!!selectedShot}
                        selectedShot={selectedShot}
                        selectedShotFrame={selectedShotFrame}
                        hasFrames={frames.length > 0}
                        currentSceneShots={currentSceneShots}
                        showShotList={showShotList}
                        onInitiateFrameGeneration={initiateFrameGeneration}
                        onClearSelection={() => selectShot(null)}
                        onRegenerateAll={() => setIsGenerationDialogOpen(true)}
                        onToggleShotList={() => setShowShotList(true)}
                        onAddFrame={handleAddFrame}
                    />

                    <div className="flex-1 overflow-hidden h-full">
                        <ScrollArea className="h-full w-full">
                            <div className="p-6 max-w-5xl mx-auto">
                                {isLoadingFrames ? (
                                    <div className="flex flex-col items-center justify-center h-[50vh]">
                                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                        <p className="mt-4 text-muted-foreground">Loading storyboard...</p>
                                    </div>
                                ) : displayedFrames.length === 0 ? (
                                    <StoryboardNoFramesState
                                        selectedShot={selectedShot}
                                        selectedShotFrame={selectedShotFrame}
                                        onToggleShotList={() => setShowShotList(true)}
                                        onInitiateFrameGeneration={initiateFrameGeneration}
                                        onGenerateAll={() => setIsGenerationDialogOpen(true)}
                                    />
                                ) : (
                                    <div className="flex flex-col gap-6 transition-all duration-300 pb-20">
                                        {displayedFrames.map((frame) => (
                                            <div
                                                id={frame.id}
                                                key={frame.id}
                                                className="transition-all duration-30"
                                            >
                                                <StoryboardFrame
                                                    frame={frame}
                                                    displayImageUrl={frameCacheBusters.get(frame.id) ?? frame.imageUrl}
                                                    onViewImage={(url) => setViewingImage(url)}
                                                    onRegenerateImage={() => handleRegenerateFrame(frame)}
                                                    isRegeneratingImage={regeneratingFrameIds.has(frame.id)}
                                                    onRegenerateFrame={() => handleRegenerateFrameAI(frame)}
                                                    isRegeneratingFrame={regeneratingFrameAIIds.has(frame.id)}
                                                    onUpdate={handleUpdateFrame}
                                                    onRemove={handleRemoveFrame}
                                                    defaultEditing={recentlyCreatedFrameId === frame.id}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </ScrollArea>
                    </div>
                </div>

                {showShotList && (
                    <div className="w-1/3 lg:w-1/4 border-l flex flex-col h-full bg-background transition-all duration-300">
                        {selectedSceneNode ? (
                            <ShotListPreviewPanel
                                sceneId={selectedSceneNode.id}
                                onClose={() => setShowShotList(false)}
                                onShotClick={(shot) => selectShot(shot)}
                                selectedShotId={selectedShot?.id}
                            />
                        ) : (
                            <div className="p-4">Select a scene</div>
                        )}
                    </div>
                )}
            </div>

            <StoryboardGenerationDialog
                open={isGenerationDialogOpen}
                onOpenChange={(open) => {
                    setIsGenerationDialogOpen(open)
                    if (!open) setGeneratingShot(null)
                }}
                shot={generatingShot}
            />

            {viewingImage && (
                <StoryboardImageLightbox
                    imageUrl={viewingImage}
                    onClose={() => setViewingImage(null)}
                />
            )}
        </div>
    )
}

export default StoryboardMainContent
